// covered-by: frontends/carrie/src/services/__tests__/askPunttProjects.test.ts
// covered-by: qa/rickrubin/tests/api/ask-puntt-projects.spec.ts
import { type AskPunttProjectScope } from "@mg/schemas/src/christo/askPunttProject";
import {
  type Query,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { askPunttKeys } from "./askPuntt";

import { useUI } from "../../contexts/ui";
import {
  type AppendProjectFeedPostRequest,
  type ListProjectTimelineResponse,
  type ProjectSuggestionResolution,
} from "../../services/askPunttProjectFeed.types";
import * as ProjectsService from "../../services/askPunttProjects";

/**
 * React Query hooks for the Ask Puntt Projects API. Mirrors the chat hooks in
 * ./askPuntt.tsx — broad prefix keys for invalidation, scope-aware lists.
 */
export const askPunttProjectKeys = {
  all: ["ask-puntt-projects"] as const,
  listsPrefix: ["ask-puntt-projects", "list"] as const,
  list: (scope?: AskPunttProjectScope) =>
    [...askPunttProjectKeys.all, "list", scope ?? "mine"] as const,
  project: (projectId: string) =>
    [...askPunttProjectKeys.all, "project", projectId] as const,
  feed: (projectId: string) =>
    [...askPunttProjectKeys.all, "feed", projectId] as const,
  timeline: (projectId: string) =>
    [...askPunttProjectKeys.all, "timeline", projectId] as const,
  reviews: (projectId: string) =>
    [...askPunttProjectKeys.all, "reviews", projectId] as const,
} as const;

/** List the requester's projects (scope: "mine" | "shared" | "all"). */
export function useAskPunttProjects(options?: {
  enabled?: boolean;
  scope?: AskPunttProjectScope;
}) {
  const scope = options?.scope ?? "mine";
  return useQuery({
    queryKey: askPunttProjectKeys.list(scope),
    queryFn: () => ProjectsService.listProjects({ limit: 100, scope }),
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch a single project with its chats (shared chat + the requester's own
 * personal chats) and full files array. Pass `refetchInterval` to poll while a
 * chat is being promoted or files change.
 */
export function useAskPunttProject(
  projectId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: askPunttProjectKeys.project(projectId || ""),
    queryFn: () => ProjectsService.getProject(projectId!),
    enabled: !!projectId && options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/** Create a project (server auto-creates its shared chat). */
export function useCreateAskPunttProject() {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: ProjectsService.createProject,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.listsPrefix,
      });
    },
    onError: (error) => {
      console.error("Failed to create project:", error);
      notify({
        title: "Failed to create project",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/** Update project metadata (owner-only). */
export function useUpdateAskPunttProject(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (payload: {
      name?: string;
      description?: string;
      context?: string;
      upcomingWork?: string;
    }) => ProjectsService.updateProject(projectId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.listsPrefix,
      });
    },
    onError: (error) => {
      console.error("Failed to update project:", error);
      notify({
        title: "Failed to update project",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Add one or more members to a project (owner-only) — backs the header
 * "+ Invite" modal. Invalidates the project so the avatar stack + member
 * count update in place.
 */
export function useAddProjectMembers(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (userIds: string[]) =>
      ProjectsService.addProjectMembers(projectId, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.listsPrefix,
      });
    },
    onError: (error) => {
      console.error("Failed to invite members:", error);
      notify({
        title: "Failed to invite",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Read a project's feed (chronological, newest-first server-side). Pass
 * `refetchInterval` to poll while a review runs or an integration syncs so
 * the timeline + composer reflect new events without a manual refresh.
 */
export function useProjectFeed(
  projectId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: askPunttProjectKeys.feed(projectId || ""),
    queryFn: () => ProjectsService.listProjectFeed(projectId!, { limit: 100 }),
    enabled: !!projectId && options?.enabled !== false,
    staleTime: 15 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Unified project feed timeline (feed events + shared-chat turns). Pass
 * `refetchInterval` to poll while Puntt is generating a reply or a review runs.
 */
export function useProjectTimeline(
  projectId: string | null,
  options?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((
          query: Query<ListProjectTimelineResponse>,
        ) => number | false | undefined);
  },
) {
  return useQuery({
    queryKey: askPunttProjectKeys.timeline(projectId || ""),
    queryFn: () =>
      ProjectsService.listProjectTimeline(projectId!, { limit: 200 }),
    enabled: !!projectId && options?.enabled !== false,
    staleTime: 5 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/** Append a user post to the project feed (composer submit). */
export function useAppendFeedPost(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (payload: AppendProjectFeedPostRequest) =>
      ProjectsService.appendProjectFeedPost(projectId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.feed(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.timeline(projectId),
      });
    },
    onError: (error) => {
      console.error("Failed to post to project feed:", error);
      notify({
        title: "Failed to post",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Share one of the requester's personal project chats with the whole project
 * (the "Share to project" action). On success refetches the project (so the
 * chat now renders as shared) and the feed (the share note appears).
 */
export function useShareProjectChat(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (chatId: string) =>
      ProjectsService.shareProjectChat(projectId, chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.feed(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.timeline(projectId),
      });
      notify({
        title: "Shared to project",
        message: "Everyone on the project can now see this chat.",
        variant: "success",
      });
    },
    onError: (error) => {
      console.error("Failed to share chat to project:", error);
      notify({
        title: "Failed to share chat",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Read the active reviews for a project (right-hand Reviews panel). Pass
 * `refetchInterval` to keep the IN REVIEW chip + version cards live while a
 * re-review runs after a new version upload.
 */
export function useProjectReviews(
  projectId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: askPunttProjectKeys.reviews(projectId || ""),
    queryFn: () => ProjectsService.getProjectReviews(projectId!),
    enabled: !!projectId && options?.enabled !== false,
    staleTime: 15 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Resolve a feed suggestion (ignore | approve | adjust). Refetches the feed
 * (the resolved suggestion flips to resolved server-side) and, on approve,
 * the reviews panel + project (an approve may invite a member or re-review).
 */
export function useResolveFeedSuggestion(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: ({
      eventId,
      action,
      payload,
    }: {
      eventId: string;
      action: ProjectSuggestionResolution;
      payload?: Record<string, unknown>;
    }) =>
      ProjectsService.resolveProjectSuggestion(projectId, eventId, {
        action,
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.feed(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.timeline(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.reviews(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
    },
    onError: (error) => {
      console.error("Failed to resolve suggestion:", error);
      notify({
        title: "Couldn't update suggestion",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Promote a standalone chat into a new project (idempotent). On success the
 * project lists + the source chat are invalidated so the new project shows
 * up in the sidebar and the chat reflects its new `projectId`.
 */
export function usePromoteChatToProject() {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (chatId: string) =>
      ProjectsService.promoteChatToProject(chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.listsPrefix,
      });
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
    },
    onError: (error) => {
      console.error("Failed to promote chat to project:", error);
      notify({
        title: "Couldn't create project",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Promote a personal chat into the project (owner-only). On success the chat
 * becomes the project's shared scope and the team is notified server-side.
 */
export function useAddChatToProject(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (chatId: string) =>
      ProjectsService.addChatToProject(chatId, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
    },
    onError: (error) => {
      console.error("Failed to share chat to project:", error);
      notify({
        title: "Failed to share chat",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}
